"use client";

import clsx from "clsx";
import type { FundingMix } from "@/lib/sse/useSSE";
import { BankBadge } from "@/components/banks/BankBadge";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 3.2 — Buyer-Side Funding tile.
 *
 *   T1: 100% buyer working capital, **no <BankBadge> rendered** — the tile
 *   shows a clean "Self-financed" mark in the Finance-pillar accent.
 *
 *   T2 / T3 reuse this same component with a non-null bankBadge — the
 *   `<BankBadge>` materialises and the funding-mix bar shows the split.
 */
export function FundingTile({
  fundingMix,
  passiveTrustLine,
  className,
}: {
  fundingMix: FundingMix | null;
  passiveTrustLine?: string;
  className?: string;
}) {
  const buyerPct = fundingMix?.buyerWorkingCapitalPct ?? 100;
  const bankPct  = fundingMix?.bankFinancingPct       ?? 0;
  const bankName = fundingMix?.bankBadge ?? null;
  const isSelfFinanced = bankPct === 0 || !bankName;
  const t = useTranslations("funding");

  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("title")}</p>
            <h3 className="scene-h2 mt-1 text-orbitra-paper">
              {isSelfFinanced ? t("buyerWorkingCapital") : t("partialFinancing")}
            </h3>
          </div>
          <span className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider",
            isSelfFinanced
              ? "border-state-financed/50 bg-state-financed/10 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]"
              : "border-state-financed/50 bg-state-financed/15 text-state-financed",
          )}>
            {isSelfFinanced ? t("selfFinancedChip") : `${bankPct}% ${bankName}`}
          </span>
        </div>

        <div className="mt-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{t("fundingMix")}</p>
          <div className="mt-2 flex h-8 overflow-hidden rounded-md border border-state-financed/30 bg-orbitra-ink/40">
            <div
              className="flex items-center justify-center bg-state-financed/30 text-[11px] text-state-financed"
              style={{ width: `${buyerPct}%` }}
            >
              {buyerPct}% {t("buyerFundsSuffix")}
            </div>
            {bankPct > 0 && (
              <div
                className="flex items-center justify-center bg-state-financed/70 text-[11px] text-orbitra-ink"
                style={{ width: `${bankPct}%` }}
              >
                {bankPct}% {bankName}
              </div>
            )}
          </div>
        </div>

        {!isSelfFinanced && bankName && (
          <div className="mt-5">
            <BankBadge name={bankName} fundingPct={bankPct} />
          </div>
        )}

        <div className="mt-auto pt-5">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("mercatorLabel")}</p>
          <p className="mt-1 text-[12px] text-orbitra-paper">
            {isSelfFinanced
              ? t("mercatorSelfFinanced")
              : `${bankPct}% ${t("mercatorPartialPrefix")} ${bankName}${t("mercatorPartialSuffix")}`}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("passiveTrustBadge")}</p>
          <p className="mt-2 text-[12px] text-orbitra-paper">
            {passiveTrustLine ?? t("buyerHistoryLine")}
          </p>
          <p className="mt-1 text-[10px] text-orbitra-muted">{t("passiveTrustDetail")}</p>
        </div>
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("effectiveCeiling")}</p>
          <p className="mt-2 text-orbitra-paper">{t("effectiveCeilingValue")}</p>
        </div>
      </div>
    </div>
  );
}
